import React, { useMemo, useContext, useCallback } from "react";
import { Card, CardActionArea, CardContent, Typography, Grid2 as Grid } from "@mui/material";
import PageContext from "../../../Common/PageContext";
import { useStyles } from "../../Search.styles";
import { SearchResultLinkStatus } from "./SearchResultLinkStatus";
import { SearchResultLinkProps } from "../../types";
import { isFeatureEnabledForLocation, isFeatureEnabledForLocationAndReceiveType } from "../../../../util/featureUtil";
import { receiveType as receiveTypes, determineReceiveType } from "../../../../util/receiveTypeMapping";

const SearchResultLink: React.FC<SearchResultLinkProps> = ({
	onSelect,
	receiveType,
	invoiceType,
	purchaseOrderNumber,
	invoiceNumber,
	searchDataSource,
	transferLabelId,
	totalQuantity,
	totalCostInc,
	status,
	onOpenCheckin,
	highlight,
}) => {
	const classes = useStyles();
	const [pageContext] = useContext(PageContext);

	const {
		userLocation,
		featureConfiguration: { arrivals, partReceive },
	} = pageContext;

	const isPartReceiveFeatureEnabled = useMemo(() => {
		return isFeatureEnabledForLocation(userLocation, partReceive);
	}, [userLocation, partReceive]);

	const determinedReceiveType = useMemo(() => {
		return determineReceiveType(
			invoiceType,
			receiveType,
			purchaseOrderNumber,
			invoiceNumber,
			status,
			isPartReceiveFeatureEnabled,
			searchDataSource
		);
	}, [
		invoiceType,
		receiveType,
		purchaseOrderNumber,
		invoiceNumber,
		status,
		isPartReceiveFeatureEnabled,
		searchDataSource,
	]);

	const isArrivalsFeatureEnabled = useMemo(() => {
		if (!determinedReceiveType) {
			return false;
		}
		return isFeatureEnabledForLocationAndReceiveType(userLocation, arrivals, determinedReceiveType);
	}, [arrivals, determinedReceiveType, userLocation]);

	const handleCheckinClick = (event) => {
		event.stopPropagation();
		if (onOpenCheckin) onOpenCheckin();
	};

	const handleSelect = useCallback(() => onSelect(determinedReceiveType), [onSelect, determinedReceiveType]);

	return (
		<Card elevation={0} square className={highlight ? classes.searchCardHighlighted : classes.searchCard}>
			<CardActionArea component="div" onClick={handleSelect}>
				<CardContent className={classes.searchCardContent}>
					<Grid container wrap="nowrap" spacing={0} size="grow">
						{invoiceNumber && (
							<Grid size={4}>
								<Typography variant="caption" color="textSecondary" component="div" className={classes.titleText}>
									Invoice No.
								</Typography>
								<Typography variant="body2" component="div" className={classes.valueText} noWrap>
									{invoiceNumber}
								</Typography>
							</Grid>
						)}
						{receiveType === receiveTypes.transfer && (
							<Grid size={4}>
								<Typography variant="caption" color="textSecondary" component="div" className={classes.titleText}>
									Label ID
								</Typography>
								<Typography variant="body2" component="div" className={classes.valueText} noWrap>
									{transferLabelId}
								</Typography>
							</Grid>
						)}
						<Grid size={1}>
							<Typography
								variant="caption"
								color="textSecondary"
								align="center"
								component="div"
								className={classes.titleText}
							>
								Qty
							</Typography>
							<Typography variant="body2" align="center" component="div" className={classes.valueText}>
								{totalQuantity}
							</Typography>
						</Grid>
						<Grid size={3}>
							{receiveType !== receiveTypes.transfer && (
								<>
									<Typography
										variant="caption"
										color="textSecondary"
										align="center"
										component="div"
										className={classes.titleText}
									>
										Total Inc
									</Typography>
									<Typography variant="body2" align="center" component="div" className={classes.valueText}>
										{totalCostInc && `$${totalCostInc.toFixed(2)}`}
									</Typography>
								</>
							)}
						</Grid>
						<Grid size={4}>
							<SearchResultLinkStatus
								status={status}
								isArrivalsFeatureEnabled={isArrivalsFeatureEnabled}
								handleCheckinClick={handleCheckinClick}
							/>
						</Grid>
					</Grid>
				</CardContent>
			</CardActionArea>
		</Card>
	);
};

export default SearchResultLink;
